import {
  useGetAdCostSegmentationConfigsQuery,
  useGetWebPageViewSegmentationConfigsQuery
} from 'app/apis/queryApi';
import { RootState } from 'app/store';
import Spinner from 'common/components/loading/spinner';
import TileGridContainer from 'features/tiles/tileGridContainer';
import TileWithoutDefaultStyling from 'features/tiles/tileWithoutDefaultStyling';
import { useDispatch, useSelector } from 'react-redux';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import SegmentationRulesTable from 'features/segmentation-config/segmentation-config-table/segmentationConfigTable';
import Button from 'common/components/buttons/button';
import {
  resetSegmentationConfig,
  selectSegmentationCategory,
  setSegmentationCategory
} from 'features/segmentation-config/segmentation-config.slice';
import { useNavigate } from 'react-router-dom';
import Tabs from 'common/components/tabs/tabs';
import { TabKey } from 'features/segmentation-config/segmentation-config-tab-keys';
import { useMemo } from 'react';
const SettingsSegmentationConfigPage = () => {
  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const MAX_AD_COST_SEGMENTATION_RULES = 30;

  const { data: webPageViewConfigData, isLoading: isWebPageViewLoading } =
    useGetWebPageViewSegmentationConfigsQuery({ siteId });
  const { data: adCostConfigData, isLoading: isAdCostLoading } =
    useGetAdCostSegmentationConfigsQuery({ siteId });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const tabConfig = [
    { key: TabKey.Forms, label: 'Web', icon: 'ra-icons-marketing-www-2' },
    {
      key: TabKey.Cost,
      label: 'Ad Platform',
      icon: 'ra-icons-marketing-dollar'
    }
  ];

  const onCreateNew = () => {
    dispatch(resetSegmentationConfig());
    dispatch(setSegmentationCategory(activeTab));
    navigate('/settings/segmentation-config/create');
  };

  const activeTab = useSelector(selectSegmentationCategory) ?? TabKey.Forms;
  const isAdCostLimitHit = useMemo(() => {
    return (
      activeTab === TabKey.Cost &&
      adCostConfigData &&
      adCostConfigData.length >= MAX_AD_COST_SEGMENTATION_RULES
    );
  }, [activeTab, adCostConfigData]);

  return (
    <TileGridContainer>
      <TileWithoutDefaultStyling className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm w-full grow basis-full">
        <section className="mx-6 my-6">
          <div className="flex items-center justify-between border-b border-neutral-200 pb-4 mb-4 mt-2">
            <h2 className="text-primary-700 text-l">Your Segments</h2>

            <div className="flex flex-col items-end">
              <Button
                identifier="create-segmentation-rule"
                theme={ButtonTheme.PRIMARY}
                onClick={onCreateNew}
                classOverrides="py-2 px-4"
                isDisabled={isAdCostLimitHit}
              >
                <span>New Segment</span>
              </Button>
              {isAdCostLimitHit && (
                <p className="text-xs text-neutral-500 mt-1">
                  You have reached the limit of {MAX_AD_COST_SEGMENTATION_RULES}
                  ad platform rules. To create a new one, please delete an
                  existing rule.
                </p>
              )}
            </div>
          </div>

          <Tabs
            tabs={tabConfig}
            activeTab={activeTab}
            onTabChange={(key) =>
              dispatch(setSegmentationCategory(key as TabKey))
            }
            textSize="text-sm"
            hoverBorderColor="border-neutral-400"
            iconSize="text-sm"
          />

          {activeTab === TabKey.Forms &&
            (isWebPageViewLoading ? (
              <div className="flex justify-center">
                <Spinner />
              </div>
            ) : webPageViewConfigData && webPageViewConfigData.length > 0 ? (
              <SegmentationRulesTable
                segmentationRules={webPageViewConfigData}
              />
            ) : (
              <div className="text-center">
                <i className="ra-icons-generic-spanner-wrench text-neutral-500 text-4xl block"></i>
                <h2 className="text-xl">No Segments Found</h2>
                <p className="mt-1 text-sm">
                  You currently have no segments configured.
                </p>
              </div>
            ))}

          {activeTab === TabKey.Cost &&
            (isAdCostLoading ? (
              <div className="flex justify-center">
                <Spinner />
              </div>
            ) : adCostConfigData && adCostConfigData.length > 0 ? (
              <SegmentationRulesTable segmentationRules={adCostConfigData} />
            ) : (
              <div className="text-center">
                <i className="ra-icons-generic-spanner-wrench text-neutral-500 text-4xl block"></i>
                <h2 className="text-xl">No Segments Found</h2>
                <p className="mt-1 text-sm">
                  You currently have no segments configured.
                </p>
              </div>
            ))}
        </section>
      </TileWithoutDefaultStyling>
    </TileGridContainer>
  );
};

export default SettingsSegmentationConfigPage;
